/**
 * Qué parte del árbol está ACTIVA en una versión concreta.
 *
 * La estructura de componentes pertenece al MODELO y la comparten todas sus
 * versiones, así que el árbol por sí solo no dice qué se evalúa en una versión
 * dada. Eso se deduce de los indicadores: un aspecto está activo si tiene
 * indicador propio en la versión, o si algún descendiente lo tiene —de modo que
 * las ramas intermedias se mantienen visibles y el árbol no queda desconectado.
 *
 * Se apoya en `GET /api/versiones/{id}/estructura`, que anota cada nodo con
 * `tiene_indicador` y `activo`.
 */

import type Componente from '@/interfaces/Componente'
import type { NodoEstructura } from '@/interfaces/EstructuraVersion'
import { esVarianteEnfoque } from '@/utils/enfoque'

/**
 * IDs de los componentes ACTIVOS en una versión, a partir de la estructura que
 * devuelve `GET /api/versiones/{id}/estructura`. Un componente está activo si
 * tiene un indicador propio en la versión o algún descendiente lo tiene (el
 * backend ya lo calcula así en el campo `activo`), por lo que el conjunto
 * incluye también a los ancestros de las hojas con indicador.
 */
export function idsComponentesActivos(estructura: NodoEstructura[]): Set<number> {
  const activos = new Set<number>()
  const recorrer = (nodos: NodoEstructura[]) => {
    for (const n of nodos) {
      if (n.activo) activos.add(n.id_componente)
      if (n.hijos_recursivos?.length) recorrer(n.hijos_recursivos)
    }
  }
  recorrer(estructura)
  return activos
}

/**
 * Poda un árbol de componentes dejando solo los activos en la versión: como la
 * estructura de componentes es del modelo y se comparte entre versiones, un
 * aspecto sin indicador en esta versión no se tiene en cuenta y se oculta.
 *
 * Si `activos` está vacío se devuelve el árbol sin podar, para no ocultar todo
 * ante un fallo de carga o una versión sin indicadores.
 */
export function podarArbolInactivo(arbol: Componente[], activos: Set<number>): Componente[] {
  if (activos.size === 0) return arbol
  return arbol
    .filter(n => n.id_componente == null || activos.has(n.id_componente))
    .map(n => ({ ...n, children: n.children ? podarArbolInactivo(n.children, activos) : [] }))
}

/**
 * Poda por enfoque: deja fuera las variantes cuyo enfoque NO coincide con el de
 * la versión. Los aspectos General (y los de enfoque coincidente) se conservan.
 * Si la versión no tiene un enfoque específico, no filtra. Útil en vistas de
 * edición (p. ej. agregar indicadores), donde aún no hay indicadores que marquen
 * "activo" pero igual deben ocultarse las variantes de otros enfoques.
 */
export function podarPorEnfoque(arbol: Componente[], versionEnfoque?: string | null): Componente[] {
  if (!versionEnfoque || versionEnfoque === 'general') return arbol
  return arbol
    .filter(n => !esVarianteEnfoque(n.enfoque) || n.enfoque === versionEnfoque)
    .map(n => ({ ...n, children: n.children ? podarPorEnfoque(n.children, versionEnfoque) : [] }))
}

/**
 * Colapsa los grupos de variante entre hermanos: de cada `grupo_variante` solo
 * conserva UN representante, el más apropiado para la versión.
 *
 * Regla de selección:
 * - Versión con enfoque específico → el miembro cuyo `enfoque` coincide;
 *   si ninguno coincide, el miembro de `enfoque='general'`.
 * - Versión sin enfoque (null / 'general') → el miembro de `enfoque='general'`;
 *   si no existe, el primero del grupo.
 *
 * Esto complementa a `podarPorEnfoque`: esa función ya elimina las variantes
 * de otros programas, pero puede dejar TANTO la general COMO la específica del
 * mismo grupo. Esta función elige solo una de las dos.
 *
 * Se aplica recursivamente a los hijos de cada nodo conservado.
 */
export function podarGruposVariante(
  hermanos: Componente[],
  versionEnfoque?: string | null
): Componente[] {
  const esEnfoqueEspecifico = versionEnfoque != null && versionEnfoque !== 'general'
  const elegidoPorGrupo = new Map<string, Componente>()

  for (const nodo of hermanos) {
    if (!nodo.grupo_variante) continue
    const grupo = nodo.grupo_variante
    const actual = elegidoPorGrupo.get(grupo)

    if (!actual) {
      elegidoPorGrupo.set(grupo, nodo)
    } else {
      const actualEsGeneral = !esVarianteEnfoque(actual.enfoque)
      const esteEsGeneral   = !esVarianteEnfoque(nodo.enfoque)

      if (esEnfoqueEspecifico) {
        // Preferir el que coincide con el enfoque de la versión
        if (actual.enfoque !== versionEnfoque && nodo.enfoque === versionEnfoque) {
          elegidoPorGrupo.set(grupo, nodo)
        }
      } else {
        // Sin enfoque específico: preferir el general sobre los específicos
        if (!actualEsGeneral && esteEsGeneral) {
          elegidoPorGrupo.set(grupo, nodo)
        }
      }
    }
  }

  return hermanos
    .filter(n => {
      if (!n.grupo_variante) return true
      return elegidoPorGrupo.get(n.grupo_variante)?.id_componente === n.id_componente
    })
    .map(n => ({
      ...n,
      children: n.children ? podarGruposVariante(n.children, versionEnfoque) : []
    }))
}
